import { Children, Fragment, useState } from "react";

import { generatePath, Link, useRouteMatch } from "react-router-dom";

import { deleteTag } from "@/api/endpoints/tags";
import ArrowRight from "@/assets/icons/arrow-right.svg";
import ChartLine from "@/assets/icons/chart-line.svg";
import ChatBubble from "@/assets/icons/chat-bubble.svg";
import CogIcon from "@/assets/icons/cog.svg";
import CubeIcon from "@/assets/icons/cube.svg";
import FavoriteIcon from "@/assets/icons/favorite.svg";
import MagnifyingGlass from "@/assets/icons/magnifying-glass.svg";
import PlusIcon from "@/assets/icons/plus.svg";
import RectangleStack from "@/assets/icons/rectangle-stack.svg";
import TagIcon from "@/assets/icons/tag.svg";
import TrashIcon from "@/assets/icons/trash.svg";
import { PLAN_TYPE } from "@/constants";
import ConfirmModal from "@/containers/ConfirmModal";
import GrowthPlanModalAE from "@/containers/GrowthPlanModalAE";
import PlanModal from "@/containers/PlanModal";
import { useAlert } from "@/contexts/AlertContext";
import { useTags } from "@/contexts/TagContext";
import { FEATURE } from "@/features";
import { useHasFeature } from "@/hooks/useHasFeature";
import { useOrganization } from "@/hooks/useOrganization";
import { INTERCOM_IDS } from "@/intercom";
import { RELATIVE_PATHS, ROUTES } from "@/routes_new";
import { cx } from "@/utils";
import { stringifyUrl } from "@/utils/query-string";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import Button from "@components/Button";
import Clickable from "@components/Clickable";
import { Feature } from "@components/Feature";
import Tooltip from "@components/Tooltip";
import Typography from "@components/Typography";

import styles from "./MainNavigation.module.css";

const Collection = ({ children, ...props }) => {
  return (
    <div {...props} className={cx(styles.collection, styles.layout)}>
      {children}
    </div>
  );
};

const Branch = ({
  icon: Icon,
  open: passedOpen = false,
  active,
  className,
  children,
  empty = false,
  label,
  to,
  ...props
}) => {
  const [open, setOpen] = useState(passedOpen);

  const inner = (
    <>
      {Icon && (
        <Icon width={23} strokeWidth={2} className={styles.branchIcon} />
      )}
      <span className={cx(styles.text, styles.overflowProtection)}>
        {label}
      </span>
    </>
  );

  if (Children.count(children) == 0 || empty) {
    // If this branch has no children, and a link of it's own, render it as a link.
    if (to) {
      return (
        <Link
          to={to}
          className={cx(className, styles.branch, {
            [styles.active]: active,
            [styles.open]: open,
          })}
          {...props}
        >
          {inner}
        </Link>
      );
    }
    // If not, return nothing.
    return null;
  }

  return (
    <>
      <Clickable
        {...props}
        onClick={() => setOpen((v) => !v)}
        className={cx(className, styles.branch, styles.expandable, {
          [styles.active]: active,
          [styles.open]: open,
        })}
      >
        {inner}
      </Clickable>
      {children && open && <div>{children}</div>}
    </>
  );
};

const SettingsLink = ({ to, ...props }) => (
  <Tooltip content={L.MENTION_SETTING_TITLE}>
    <Link
      to={to}
      className={styles.action}
      data-testid="settings-link"
      data-intercom={INTERCOM_IDS.MAIN_MENU_ALERT_SETTINGS}
    >
      <CogIcon width={24} {...props} strokeWidth="1.5px" />
    </Link>
  </Tooltip>
);

const RemoveTag = ({ onClick, className, ...props }) => (
  <Tooltip content={L.MENTIONS_TAGS_TAG_DELETE}>
    <Clickable
      onClick={onClick}
      className={cx(styles.action, className)}
      data-testid="delete-tag-button"
    >
      <TrashIcon height={24} {...props} strokeWidth="1.5px" />
    </Clickable>
  </Tooltip>
);

const Apart = () => <span className={styles.apart} />;

const Leaf = ({
  id = null,
  to,
  action,
  label,
  active,
  icon,
  has_unread,
  ...props
}) => {
  return (
    <div
      className={cx(styles.leaf, {
        [styles.active]: active,
        [styles.unread]: has_unread,
        unread:
          has_unread /** this class does nothing other than testing. - had no good idea for how to use different test-ids for one element */,
      })}
      data-testid="nav-alert-item"
    >
      <Link
        className={cx(styles.link, styles.overflowProtection, {
          [styles.active]: active,
        })}
        to={to}
        {...(id ? { id: id } : {})}
        {...props}
      >
        {icon && <div>{icon}</div>}
        <div className={cx(styles.label, styles.overflowProtection)}>
          {label}
        </div>
      </Link>
      {(active && action) || null}
    </div>
  );
};

const AlertLeafs = ({ alerts = [], appendParams = {}, baseRoute }) => {
  const uuid = useRouteMatch(baseRoute)?.params?.uuid;
  const { data } = useOrganization();
  const { uuid: orgUuid } = data?.ok ? data.data : {};

  if (alerts.length === 0 || !orgUuid) return null;

  const hasAppendParams = Object.values(appendParams).length > 0;

  return alerts.map((item) => {
    const basePath = generatePath(baseRoute, { uuid: item.uuid, orgUuid });
    let query = {};
    if (hasAppendParams) {
      Object.keys(appendParams).forEach((key) => {
        query[key] = item[key];
      });
    }
    return (
      <Leaf
        key={item.uuid}
        to={stringifyUrl({
          url: basePath,
          query,
        })}
        action={
          <AccessControl level={ACCESS_LEVELS.USER}>
            <SettingsLink to={`${basePath}${RELATIVE_PATHS.ALERT_SETTINGS}`} />
          </AccessControl>
        }
        label={item.title}
        has_unread={item.has_unread}
        aria-selected={item.uuid === uuid}
        active={item.uuid === uuid}
      />
    );
  });
};

const CreateNewAlertButton = () => {
  const active = useRouteMatch(ROUTES.MENTION_NEW_ORGID);
  const { data } = useOrganization();
  const { uuid } = data?.ok ? data.data : {};
  if (!uuid) return null;

  return (
    <Branch
      id="create-new-alert-button"
      to={generatePath(ROUTES.MENTION_NEW_ORGID, { orgUuid: uuid })}
      label={L.NAV_CREATE_NEW_ALERT}
      active={active}
      icon={PlusIcon}
      data-intercom={INTERCOM_IDS.MAIN_MENU_CREATE_NEW}
    />
  );
};

const Favorites = () => {
  const active = useRouteMatch(ROUTES.FAVORITES_ORGID);
  const { data } = useOrganization();
  const { uuid } = data?.ok ? data.data : {};

  if (!uuid) return null;

  return (
    <Branch
      id="favorites-button"
      to={generatePath(ROUTES.FAVORITES_ORGID, { orgUuid: uuid })}
      label={L.NAV_FAVORITE_ALERTS}
      active={active}
      icon={FavoriteIcon}
      data-intercom={INTERCOM_IDS.MAIN_MENU_FAVORITES}
    />
  );
};

const Tags = () => {
  const { organizationTags } = useTags();
  const isTagEmptyView = useRouteMatch(ROUTES.TAGS_ORGID)?.isExact;
  const tag = useRouteMatch(ROUTES.TAG_ORGID)?.params?.slug;
  const noTags =
    organizationTags?.length === 0 ||
    (organizationTags?.length === 1 &&
      organizationTags[0]?.name === "favorited");

  const { data } = useOrganization();
  const { uuid: orgUuid } = data?.ok ? data.data : {};

  const [removeTagOpen, setRemoveTagOpen] = useState(false);

  const handleDelete = async () => {
    await deleteTag(tag);
    window.location.href = generatePath(ROUTES.MENTION_LIST_ORGID, { orgUuid });
  };

  if (!orgUuid) return null;

  return (
    <>
      <Branch
        data-intercom={INTERCOM_IDS.MAIN_MENU_TAGS}
        data-testid="tags-section"
        active={isTagEmptyView}
        label={L.NAV_TAGS}
        icon={TagIcon}
        empty={noTags}
        to={generatePath(ROUTES.TAGS_ORGID, { orgUuid })}
        open={Boolean(tag)}
      >
        {organizationTags.map(({ name, slug }) =>
          name === "favorited" ? null : (
            <Leaf
              data-testid="tags-item"
              key={name}
              label={name}
              action={
                tag !== "favorited" && (
                  <AccessControl>
                    <RemoveTag
                      width={22}
                      height={22}
                      onClick={() => setRemoveTagOpen(true)}
                    />
                  </AccessControl>
                )
              }
              to={generatePath(ROUTES.TAG_ORGID, { slug, orgUuid })}
              active={slug === tag}
            />
          )
        )}
      </Branch>
      {tag && (
        <ConfirmModal
          title={L.MENTIONS_TAGS_TAG_DELETE}
          message={L.MENTIONS_TAGS_TAG_CONFIRM_DELETION}
          show={removeTagOpen}
          dangerous
          onConfirm={handleDelete}
          onCancel={() => setRemoveTagOpen(false)}
          confirmText={L.MENTIONS_TAGS_TAG_CONFIRM_DELETION_CTA}
        />
      )}
    </>
  );
};

const SavedSearches = () => {
  const {
    groupedAlerts: { saved },
  } = useAlert();

  const active = useRouteMatch(ROUTES.SAVED_SEARCHES_ORGID);
  const { data } = useOrganization();
  const { uuid: orgUuid } = data?.ok ? data.data : {};
  if (!orgUuid) return null;

  return (
    <Branch
      label={L.NAV_SAVED_MENTIONS}
      icon={RectangleStack}
      active={Boolean(active)}
      to={generatePath(ROUTES.SAVED_SEARCHES_ORGID, { orgUuid })}
      empty={saved.length === 0}
      open={Boolean(active)}
    >
      <AlertLeafs
        baseRoute={ROUTES.SAVED_SEARCHES_DETAIL_ORGID}
        alerts={saved}
        appendParams={{ start_date: true, end_date: true }}
      />
    </Branch>
  );
};

const Archive = () => {
  const {
    groupedAlerts: { inactive },
  } = useAlert();

  const active = useRouteMatch(ROUTES.ARCHIVE_ORGID);
  const { data } = useOrganization();
  const { uuid: orgUuid } = data?.ok ? data.data : {};
  if (!orgUuid) return null;

  return (
    <Branch
      label={L.NAV_EXPIRED_ALERTS}
      icon={CubeIcon}
      active={Boolean(active)}
      to={generatePath(ROUTES.ARCHIVE_ORGID, { orgUuid })}
      empty={inactive.length === 0}
      open={Boolean(active)}
    >
      <AlertLeafs
        baseRoute={ROUTES.ARCHIVE_DETAIL_ORGID}
        alerts={inactive}
        appendParams={{ start_date: true, end_date: true }}
      />
    </Branch>
  );
};

const Reports = () => {
  const active = useRouteMatch(ROUTES.REPORTS_ORGID);

  const { data } = useOrganization();
  const { uuid: orgUuid } = data?.ok ? data.data : {};
  if (!orgUuid) return null;

  return (
    <>
      <Branch
        id="reports-button"
        to={generatePath(ROUTES.REPORTS_ORGID, { orgUuid })}
        label={L.NAV_REPORTS}
        active={active !== null}
        icon={ChartLine}
        data-intercom={INTERCOM_IDS.MAIN_MENU_REPORTS}
      />
    </>
  );
};

const Explore = () => {
  const active = useRouteMatch(ROUTES.SEARCH_ORGID);

  const { data } = useOrganization();
  const { uuid: orgUuid } = data?.ok ? data.data : {};
  if (!orgUuid) return null;

  return (
    <Branch
      id="explore-button"
      data-testid="explore-button"
      to={generatePath(ROUTES.SEARCH_ORGID, { orgUuid })}
      label={L.NAV_SEARCH}
      active={active !== null}
      icon={MagnifyingGlass}
      data-intercom={INTERCOM_IDS.MAIN_MENU_EXPLORE}
    />
  );
};

const Alerts = () => {
  const { groupedAlerts } = useAlert();
  // This one is suffixed with all, so matches both mentions/all and just mentions/
  const open = useRouteMatch(ROUTES.MENTION_DETAIL_ORGID);
  // ... Therefore, check if we're on the mentions/all page as well
  const slug = useRouteMatch(ROUTES.TAG_ORGID)?.params?.slug;

  return (
    <Branch
      data-intercom={INTERCOM_IDS.MAIN_MENU_ALERTS}
      open={open && !slug}
      label={L.NAV_ACTIVE_ALERTS}
      icon={ChatBubble}
      data-testid="mention-side-nav"
    >
      <AlertLeafs
        alerts={groupedAlerts.active}
        baseRoute={ROUTES.MENTION_DETAIL_ORGID}
      />
    </Branch>
  );
};

const InviteColleague = () => {
  const { data } = useOrganization();
  const { uuid: orgUuid } = data?.ok ? data.data : {};
  if (!orgUuid) return null;

  return (
    <div className={styles.invite}>
      <Typography variant="subtitle-2" className={styles["invite__title"]}>
        {L.ADD_USER}
      </Typography>
      <Typography variant="body-1" className={styles["invite__description"]}>
        {L.ADD_USER_TEXT}
      </Typography>

      <Link to={generatePath(ROUTES.INVITE_USER_ORGID, { orgUuid: orgUuid })}>
        <Button
          size="sm"
          type="button"
          icon={ArrowRight}
          variant="tertiary"
          iconPlacement="after"
          className={styles["invite__cta"]}
          data-testid="invite-colleague-link"
        >
          {L.ADD_USER}
        </Button>
      </Link>
    </div>
  );
};

const Upgrade = (props) => {
  return (
    <div className={styles.upgrade}>
      <Typography variant="subtitle-2" className={styles["upgrade__title"]}>
        {L.NAV_UPGRADE_TITLE}
      </Typography>
      <Typography variant="body-1" className={styles["upgrade__description"]}>
        {L.NAV_UPGRADE_DESC}
      </Typography>

      <Button
        size="sm"
        type="button"
        icon={ArrowRight}
        variant="tertiary"
        iconPlacement="after"
        onClick={props.onClickUpgrade}
        className={styles["upgrade__cta"]}
      >
        {L.NAV_UPGRADE_CTA}
      </Button>
    </div>
  );
};

const MainNavigation = () => {
  const { data: orgData } = useOrganization();
  const { is_from_growth_app = false, plan = null } = orgData?.ok
    ? orgData.data
    : {};
  const canManageAccount = useHasFeature(FEATURE.CAN_MANAGE_ACCOUNT);

  const [plansModalOpen, setPlansModalOpen] = useState(false);

  return (
    <>
      <div data-testid="sidebar-nav">
        <Collection>
          <Alerts />
          <AccessControl>
            <CreateNewAlertButton />
          </AccessControl>
          <Archive />
        </Collection>
        <Collection data-testid="explore-collection">
          <Feature required={FEATURE.HAS_SEARCH_PRODUCT} upsale>
            <AccessControl>
              <Explore />
              <SavedSearches />
            </AccessControl>
          </Feature>
        </Collection>
        <Collection>
          <Favorites />
          <Tags />
        </Collection>
        <Collection data-testid="reports-collection">
          <Feature required={FEATURE.HAS_REPORT_PRODUCT} upsale>
            <Reports />
          </Feature>
        </Collection>

        {canManageAccount && plan?.type === PLAN_TYPE.FREE ? (
          <Fragment>
            <Apart />
            <Upgrade onClickUpgrade={() => setPlansModalOpen(true)} />
          </Fragment>
        ) : (
          <AccessControl>
            <InviteColleague />
          </AccessControl>
        )}
      </div>

      {is_from_growth_app && plan?.type === PLAN_TYPE.US_FREE ? (
        <GrowthPlanModalAE show={true} backdrop="static" />
      ) : (
        <PlanModal
          show={plansModalOpen}
          onHide={() => setPlansModalOpen(false)}
        />
      )}
    </>
  );
};

export { MainNavigation };
